import { snakeCase } from 'lodash';

import useAuth, { tokenName } from '@/useAuth';
import useAxios from '@/useAxios';
import siteConfig from './siteConfig';
import report from './report';
import sponsors from './sponsor';
import dashboard from './dashboard';
import user from './user';
import mail from './mail';
import store from './store';
import product from './product';
import live from './live';
import stream from './stream';
import quiz from './quiz';

const { axios, setAuthorization } = useAxios();
const { getMe } = useAuth();

export function sortParamToQuery(sortParam: { field: string; order: string }) {
  if (!sortParam) return '';
  return `&sort=${sortParam.order === 'DESC' ? '-' : ''}${sortParam.field}`;
}

export function sortParamToQueryCompatibleWithTable(sortParams: { field: string; order: string }[]): string {
  let result = '';
  if (sortParams.length === 0 || sortParams[0].field === undefined) return '';
  result = `${result}&sort=`;
  sortParams.forEach((sortParam: { field: string; order: string }, index: number) => {
    if (index !== 0) result = `${result},`;
    result = `${result}${sortParam.order === 'DESC' ? '-' : ''}${sortParam.field}`;
  });
  return result;
}

export function filterValueToQuery(filter: any) {
  if (!filter) return '';
  let result = '';
  Object.entries(filter).forEach(([key, value]: any[]) => {
    if (key === 'orderBy') result += sortParamToQueryCompatibleWithTable(value);
    else if (value !== null && value !== '' && value !== undefined) result = `${result}&${key}=${value}`;
  });
  return result;
}

export function camelObjectToSnakeObject(object: any, except: string[] = []) {
  const output = {};
  Object.keys(object).forEach((key: string) => {
    let newKey = key;
    if (!((object as any)[newKey] || (object as any)[newKey] === false || (object as any)[newKey] === '') && (object as any)[newKey] !== 0) return;
    if (!(except.indexOf(key) > -1)) newKey = snakeCase(key);

    if (typeof object[key] === 'object' && !Array.isArray(object[key])) {
      // To check is recent variable is type of object and not array
      (output as any)[newKey] = camelObjectToSnakeObject(object[key], except);
      // ---
    } else if (Array.isArray(object[key])) {
      // In case of recent variable is type of Array, then loop through whole array element;
      (output as any)[newKey] = object[key].map((elm: any) => {
        // If inside of Array is object, call nested;
        if (typeof elm === 'object') return camelObjectToSnakeObject(elm, except);
        // if not just return;
        return elm;
      });
    } else {
      (output as any)[newKey] = object[key];
    }
  });
  return output;
}

export const {
  getConfigs,
  updateWebsiteConfig,
  deleteFileConfig,
} = siteConfig(axios);

export const {
  getChatAsCSV,
  getVoteAsCSV,
  getQAAsCSV,
  getAllQAAsCSV,
  getVisitorAsCSV,
} = report(axios);

export const {
  getSponsors,
  createSponsor,
  updateSponsor,
  deleteSponsors,
} = sponsors(axios);

export const {
  register,
  getVisitors,
  getVisitor,
  getVisitorPin,
  updateVisitor,
  activateVisitor,
  sendConfirmRegisterEmailToVisitor,
  deleteVisitor,
  deleteVisitors,
  forceLogout,

  registrationTypeList,
  registrationTypeDisplay,
  visitorTypeTranslate,
} = user(axios);

export const {
  getEmail,
  updateEmailSubject,
  updateEmail,
  deleteLanguage,
  deleteEmailImage,
} = mail(axios);

export const {
  newStore,
  getStores,
  getStore,
  deleteStore,
  updateStore,
  deleteStoreImage,
} = store(axios);

export const {
  newProduct,
  getProducts,
  getProduct,
  updateProduct,
  deleteProduct,
  deleteProductImage,
} = product(axios);

export const {
  getLives,
  getLive,
  createLive,
  updateLive,
  deleteLive,
  updateLiveModuleStatus,
  createStream,
  updateStream,
  deleteStream,
} = live(axios);

export const {
  uploadThumbnail,
} = stream(axios);

export const { getUsersStats } = dashboard(axios);

export const login = async (
  email: string,
  password: string,
) => {
  const token = await axios.post('/adminLogin', {
    email,
    password,
  }).then((response) => {
    if (response.status === 200) {
      if (response.data.data) return response.data.data.token;
    }
    return '';
  }).catch((response) => '');

  if (token !== '') {
    const tokenStr = token.substring(token.indexOf('|') + 1);
    localStorage.setItem(tokenName, tokenStr);
    await setAuthorization(tokenStr);
    await getMe(true, axios as any);
    return true;
  }
  return false;
};

export const changePassword = async (
  password: string,
  oldPassword: string,
) => {
  const response = await axios.post('/change-password', {
    password,
    oldPassword,
  });

  try {
    if (response.status === 200) {
      return true;
    }
  } catch (e) {
    return 'error';
  }
  return 'error';
};

export const checkInformationExists = async (
  email: string | null,
  telephone: string | null,
) => {
  const axiosResult = await axios.post('/info-exists', {
    email,
    telephone,
  });
  if (axiosResult.status === 200) {
    return axiosResult.data;
  }
  return {
    email: 'failed',
    telephone: 'failed',
  };
};

/**
 * Agenda
 */
export const getAgendas = async (
  page = 1,
  perPage = 30,
  sort: { field: string; order: string },
  filterByName = '',
  filterByStatus = '',
) => {
  const result = await axios.get(`/agenda?page[number]=${page}&page[size]=${perPage}${sortParamToQuery(sort)}&filterByStatus=${filterByStatus}&filterByName=${filterByName}`)
    .then((response) => {
      if (response.status === 200) {
        return response.data;
      }
      return response;
    });
  return result;
};

export const getAgenda = async (
  id: number,
) => {
  const result = await axios.get(`/agenda/${id}`)
    .then((response) => {
      if (response.status === 200) {
        return response.data.data.agenda;
      }
      return response;
    });
  return result;
};

export const createAgenda = async (
  payload: any,
) => {
  const result = await axios.post('/agenda', {
    ...payload,
  }).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export const updateAgenda = async (
  id: number,
  payload: any,
) => {
  const result = await axios.put(`/agenda/${id}`, {
    ...payload,
  }).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export const controlAgenda = async (
  liveId: number,
  agendaId: number | null,
) => {
  const result = await axios.put(`/sessions/${liveId}`, {
    /* eslint-disable */
    active_agenda_id: agendaId,
    /* eslint-enable */
  }).then((response) => {
    if (response.status === 200) {
      return response.data.data.session.active_agenda_id;
    }
    return response;
  });
  return result;
};

export const deleteAgenda = async (
  id: number,
) => {
  const result = await axios.delete(`/agenda/${id}`).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export const deleteAgendas = async (
  ids: number[],
) => {
  const result = await axios.post('/agenda/delete', {
    ids,
  }).then((response: any) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export const activeAgenda = async (
  id: number | string,
) => {
  const result = await axios.post(`/agenda/acitve/${id}`).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

/**
 * Vote
 */
export const getVotes = async (
  page = 1,
  perPage = 30,
  sort: { field: string; order: string },
  filterByName = '',
  filterByStatus = '',
) => {
  const result = await axios.get(`/polls?page[number]=${page}&page[size]=${perPage}${sortParamToQuery(sort)}&filterByStatus=${filterByStatus}&filterName=${filterByName}`)
    .then((response) => {
      if (response.status === 200) {
        return response.data;
      }
      return response;
    });
  return result;
};

export const getVote = async (
  id: number,
) => {
  const result = await axios.get(`/polls/${id}`).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export const createVote = async (
  liveId: number | string, // Actually is Session id
  payload: {
    name: string;
    name_en: string;
    start_time: string;
    display_time: string;
    scheduled_time: string;
    auto_display: boolean;
    show_result: boolean;
    resubmit: boolean;
    automated_answer: string;
    automated_answer_en: string;
    text: string;
    text_en: string;
    poll_type: string;
    choices: { id: number; text: string; text_en: string }[];
  },
) => {
  const result = await axios.post(`/sessions/${liveId}/polls`, payload).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export const updateVote = async (
  id: number,
  payload: {
    name: string;
    name_en: string;
    start_time: string;
    display_time: string;
    scheduled_time: string;
    auto_display: boolean;
    show_result: boolean;
    resubmit: boolean;
    automated_answer: string;
    automated_answer_en: string;
    text: string;
    text_en: string;
    poll_type: string;
    choices: { id: number; text: string; text_en: string }[];
    deleteChoices: number[] | undefined;
  },
) => {
  const result = await axios.put(`/polls/${id}`, payload).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export const controlVote = async (
  liveId: number,
  pollId: number | null,
) => {
  const result = await axios.put(`/sessions/${liveId}`, {
    /* eslint-disable */
    active_poll_id: pollId,
    /* eslint-enable */
  }).then((response) => {
    if (response.status === 200) {
      return response.data.data.session.active_poll_id;
    }
    return response;
  });
  return result;
};

export const deleteVote = async (
  id: number,
) => {
  const result = await axios.post('/polls/delete', {
    ids: [id],
  }).then((response: any) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export const deleteVotes = async (
  ids: number[],
) => {
  // const params = deleteIdsToQuery(ids);
  const result = await axios.post('/polls/delete', {
    ids,
  }).then((response: any) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

// Quiz & Leaderboard
export const {
  getQuizzes,
  getQuiz,
  createQuiz,
  updateQuiz,
  controlQuiz,
  deleteQuiz,
  deleteQuizzes,
  getLeaderboards,
  resetLeaderboards,
  downloadLeaderboardAsCSV,
} = quiz(axios);

/**
 * SPEAKER
 */
export const getSpeakers = async (
  page = 1,
  perPage = 30,
  sort: { field: string; order: string },
) => {
  const result = await axios.get(`/speakers?page[number]=${page}&page[size]=${perPage}${sortParamToQuery(sort)}`)
    .then((response) => {
      if (response.status === 200) {
        return response.data;
      }
      return response;
    });
  return result;
};

export const getSpeaker = async (id: number) => {
  const result = await axios.get(`/speakers/${id}`).then((response) => {
    if (response.status === 200) {
      return response.data.data.speaker;
    }
    return response;
  });
  return result;
};

export const createSpeaker = async (payload: any) => {
  const result = await axios.post('/speakers', {
    ...payload,
  }).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export const updateSpeaker = async (id: number, payload: any) => {
  const result = await axios.put(`/speakers/${id}`, {
    ...payload,
  }).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export const deleteSpeaker = async (id: number) => {
  const result = await axios.delete(`/speakers/${id}`).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export const deleteSpeakers = async (ids: number[]) => {
  const result = await axios.post('/speakers/delete', {
    ids,
  }).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

/**
 * QUESTION AND ANSWER
 */
export const getUsersWithQuestion = async (
  page = 1,
  perPage = 30,
  sort: { field: string; order: string },
) => {
  const result = await axios.get('/questions/users')
    .then((response) => {
      if (response.status === 200) {
        return response.data.data;
      }
      return response;
    });
  return result;
};

export const getUserWithQuestions = async (
  id: number,
  page: number,
  perPage: number,
  sort: { field: string; order: string },
) => {
  const result = await axios.get(`/users/${id}/questions?page[number]=${page}&page[size]=${perPage}${sortParamToQuery(sort)}`).then((response) => {
    if (response.status === 200) {
      return response.data.data.questions;
    }
    return response;
  });
  return result;
};

export const answerQuestion = async (
  message: string,
  sessionId: number,
  userId: number,
) => {
  const result = await axios.post('/questions/answer', {
    message,
    sessionId,
    userId,
  }).then((response) => {
    if (response.status === 200) {
      return response.data.data.question;
    }
    return response;
  });
  return result;
};

export const createQuestionAnswer = async (payload: any) => {
  const result = await axios.post('/speakers', {
    ...payload,
  }).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export const updateQuestionAnswer = async (id: number, payload: any) => {
  const result = await axios.put(`/speakers/${id}`, {
    ...payload,
  }).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export const deleteQuestionAnswer = async (id: number | string) => {
  const response = await axios.delete(`/questions/${id}`);
  try {
    if (response.status === 200) {
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
};

export const deleteQuestionAnswers = async (
  userId: number | string,
  sessionId: number | string,
) => {
  const response = await axios.delete(`/questions/clear/${userId}/${sessionId}`);
  try {
    if (response.status === 200) {
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
};

/**
 * USER but ADMIN API
 */
export const getUsers = async (
  page = 1,
  perPage = 30,
  sort: { field: string; order: string },
  filterRoleId: number | string,
  filterName = '',
) => {
  const result = await axios.get(`/users?admin=true&page[number]=${page}&page[size]=${perPage}${sortParamToQuery(sort)}&filterRoleId=${filterRoleId}${filterName !== '' ? `&filterName=${filterName}` : ''}`)
    .then((response) => {
      if (response.status === 200) {
        return response.data;
      }
      return response;
    });
  return result;
};

export const getUser = async (
  id: number,
) => {
  const result = await axios.get(`/users/${id}`).then((response) => {
    if (response.status === 200) {
      return response.data.data;
    }
    return response;
  });
  return result;
};

export const createUser = async (
  name: string,
  email: string,
  telephone: string,
  organization: string,
  online: boolean,
  roles: string[] | number[],
  admin = false,
  employeeCode = '',
  password: string,
) => {
  const result = await axios.post('/users', {
    name,
    email,
    telephone,
    organization,
    online,
    admin: admin === false ? undefined : admin,
    employeeCode: employeeCode !== '' ? employeeCode : undefined,
    roles,
    password,
  }).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });

  return result;
};

export const updateUser = async (
  id: string | number,
  name: string,
  email: string,
  telephone: string,
  organization: string,
  online: boolean,
  password: string,
  admin = false,
  employeeCode = '',
) => {
  const result = await axios.put(`/users/${id}/edit`, {
    name,
    email,
    telephone,
    organization,
    admin,
    employeeCode: employeeCode !== '' ? employeeCode : undefined,
    password,
  }).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export const updateUserRole = async (
  id: string | number,
  roles: [],
) => {
  const result = await axios.post(`/users/${id}/role`, {
    roles,
  }).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export const deleteUser = async (
  ids: number[] | string[],
) => {
  const result = await axios.post('/users/delete', {
    ids,
  }).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export const deleteUsers = async (
  ids: number[],
) => {
  const result = await axios.post('/users/delete', {
    ids,
  }).then((response: any) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

/**
 * ROLE
 */
export const getRoles = async () => {
  const result = await axios.get('/roles').then((response: any) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export const getRole = async (id: string | number) => {
  const result = await axios.get(`/roles/${id}`).then((response: any) => {
    if (response.status === 200 && response.data.success) {
      return response.data.data;
    }
    return response;
  });
  return result;
};

export const createRole = async (
  role: string,
) => {
  const result = await axios.post('/roles', {
    name: role,
  }).then((response: any) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export const updateRolePermission = async (
  id: number | string,
  permissions: number[] | string[],
) => {
  const result = await axios.post(`/roles/${id}/permissions`, {
    permissions,
  }).then((response: any) => {
    if (response.status === 200) {
      return response.data;
    }
    return 'update permission failed';
  });
  return result;
};

export const updateRole = async (
  id: number | string,
  name: string | null,
  permissions: number[] | string[] | null,
) => {
  let updateRoleResult = null;
  let updatePermissionResult = null;
  if (name !== null) {
    updateRoleResult = await axios.put(`/roles/${id}`, {
      name,
    }).then((response: any) => {
      if (response.status === 200) {
        return response.data;
      }
      return false;
    });
  }

  if (permissions !== null) {
    updatePermissionResult = await updateRolePermission(id, permissions);
  }

  if (updateRoleResult && updatePermissionResult.success) return updatePermissionResult.role;
  return 'failed';
};

export const deleteRole = async (
  id: number,
) => {
  const result = await axios.delete(`/roles/${id}`).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export const getPermissions = async () => {
  const result = await axios.get('/permissions').then((response: any) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

/**
 * UTILITY
 */

export const activateStatus = async (
  id: number | string,
  entity: string,
) => {
  let url = `/sessions/${id}/activate`;
  if (entity === 'speaker') url = `/speakers/${id}/activate`;
  const result = await axios.post(url).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

/**
 * GET CHAT MESSAGEs
 * @param sessionId
 */
export const getChatMessages = async (
  sessionId: number,
  page: number,
  perPage: number,
  sort: { field: string; order: string },
) => {
  const result = await axios.get(`/sessions/${sessionId}/chat?page[number]=${page}&page[size]=${perPage}${sortParamToQuery(sort)}`).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return 'error';
  });
  return result;
};

/**
 * CHAT SUBMISSION
 * @param sessionId
 * @param message
 */
export const submitMessageToSession = async (
  sessionId: number,
  message: string,
) => {
  const result = await axios.post(`/sessions/${sessionId}/chat`, {
    message,
  }).then((response) => {
    if (response.status === 200) {
      return 'success';
    }
    return 'error';
  });
  return result;
};

export const deleteChats = async (
  sessionId: number | string,
) => {
  const response = await axios.delete(`/chat/clear/${sessionId}`);

  try {
    if (response.status === 200) {
      return response.status;
    }
    return 'error';
  } catch (e) {
    return 'error';
  }
  return 'error';
};

// export const submitMessageToPrivateQA = async (
//   questions
// ) => {

// }

/**
 * DELETE CHAT
 * @param chatId
 */
export const deleteChatMessageInSession = async (
  chatId: number | string,
) => {
  const result = await axios.delete(`/chat/${chatId}`).then((response) => {
    if (response.status === 200) {
      return 'success';
    }
    return 'error';
  });
  return result;
};

/**
 *
 */
export const getDocuments = async (
  liveSession: string | number,
  sort: { field: string; order: string },
) => {
  const result = await axios.get(`/documents?filterLiveSession=${liveSession}${sortParamToQuery(sort)}`).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export const deleteDocuments = async (
  ids: number[] | string[],
) => {
  const result = await axios.post('/documents/delete', {
    ids,
  }).then((response) => {
    if (response.status === 200) {
      return response.data;
    }
    return response;
  });
  return result;
};

export default {
  axios,
};
